import globalAgent from 'src/agent';

import type { AxiosPromise } from 'axios';
import type { ServiceSpecializationCode } from 'src/components/ServiceSpecializationPicker';

export interface ActivePackagePromotion {
    code: string;
    title: string;
    badge_text?: string;
    description?: string;
    applies_to?: string;
    multiplier: string | number;
    multiplier_label?: string;
    is_global?: boolean;
    category_id?: number | null;
}

export interface LeadCategory {
    id: number;
    name: string;
    description: string;
    icon: string;
    color: string;
    is_active: boolean;
    is_available_for_packages: boolean;
    is_delivery_enabled: boolean;
    availability_hint?: string;
    package_pricing_profile?: string | null;
    package_pricing_profile_label?: string | null;
    package_base_price_per_lead?: string | null;
    package_recommended_size?: number | null;
    active_package_promotion?: ActivePackagePromotion | null;
    lead_base_total_count?: number;
    lead_base_recent_count?: number;
    lead_base_90d_count?: number;
    lead_base_monthly_estimate?: number;
}

export interface LeadPackagePrice {
    id: number;
    category: number;
    package_size: number;
    price_per_lead: string;
    monthly_price_per_lead?: string;
    one_time_price_per_lead?: string;
    discount_percent?: number | null;
    is_active: boolean;
}

export interface LeadPricingOffer {
    id: string;
    code: string;
    kind: string;
    title: string;
    subtitle?: string;
    description?: string;
    badge_text?: string;
    is_recommended: boolean;
    currency: string;
    delivery_mode: string;
    price?: string | null;
    price_from?: string | null;
    old_price?: string | null;
    billing_period_months?: number | null;
    access_duration_days?: number | null;
    package_options?: LeadPackagePrice[];
    metadata?: Record<string, any>;
}

export interface LeadStreamFilterExample {
    id?: number;
    message_id?: number;
    text: string;
    reason?: string;
}

export interface LeadStreamFilterProfile {
    schema_version?: string;
    source?: string;
    customer_description?: string;
    offer?: string;
    geo?: string;
    category?: {
        id?: number | null;
        name?: string;
        description?: string;
    };
    strict_category_match?: boolean;
    target_intents?: string[];
    exclude?: string[];
    positive_examples?: LeadStreamFilterExample[];
    negative_examples?: LeadStreamFilterExample[];
    maybe_examples?: LeadStreamFilterExample[];
    feedback_counters?: {
        good?: number;
        bad?: number;
    };
    feedback_revision?: number;
    updated_from_feedback_at?: string;
    compiled_at?: string;
    service_specialization_codes?: ServiceSpecializationCode[];
    service_specialization_scope_declared?: boolean;
    service_specialization_unknown_codes?: string[];
    service_specialization_scope_invalid?: boolean;
}

export const getCategories = (): AxiosPromise<LeadCategory[]> => {
    return globalAgent.get(`/streams/categories/`);
};

export const getCategoryPrices = (categoryId: number): AxiosPromise<LeadPackagePrice[]> => {
    return globalAgent.get(`/streams/categories/${categoryId}/prices/`);
};

export const getCategoryOffers = (categoryId: number): AxiosPromise<LeadPricingOffer[]> => {
    return globalAgent.get(`/streams/categories/${categoryId}/offers/`);
};

export interface CreateStreamRequest {
    category_id: number;
    name?: string;
    prompt?: string;
    filter_profile?: LeadStreamFilterProfile;
    project?: number | null;
}

export interface Stream {
    id: number;
    user: number;
    category: LeadCategory;
    name: string | null;
    display_name?: string;
    balance: number;
    prompt: string | null;
    filter_profile?: LeadStreamFilterProfile | null;
    prompt_version?: string;
    prompt_compiled_at?: string | null;
    is_active: boolean;
    status?: 'pending' | 'active' | 'paused' | 'ended';
    status_label?: string;
    created_at: string;
    updated_at: string;
    package_size?: number;
    approved_deliveries?: number;
    active_access_offer_kind?: string | null;
    active_access_offer_name?: string | null;
    access_activated_at?: string | null;
    access_expires_at?: string | null;
    has_active_access?: boolean;
    fixed_price_per_lead?: string | number | null;
    fixed_total_price?: string | number | null;
    fixed_package_size?: number | null;
}

export interface UpdateStreamRequest {
    name?: string | null;
    prompt?: string | null;
    filter_profile?: LeadStreamFilterProfile | null;
    is_active?: boolean;
    project?: number | null;
}

export const createStream = (data: CreateStreamRequest): AxiosPromise<Stream> => {
    return globalAgent.post(`/streams/streams/`, data);
};

export const getStream = (streamId: number, signal?: AbortSignal): AxiosPromise<Stream> => {
    return globalAgent.get(`/streams/streams/${streamId}/`, { signal });
};

export const updateStream = (streamId: number, data: UpdateStreamRequest): AxiosPromise<Stream> => {
    return globalAgent.patch(`/streams/streams/${streamId}/`, data);
};

export interface StreamTrainingExample {
    id: number;
    text: string;
    choice: 'fit' | 'skip' | 'maybe' | null;
    reason?: string;
}

export const trainStream = (
    streamId: number,
    expectedUpdatedAt: string,
    examples: StreamTrainingExample[]
): AxiosPromise<Stream> =>
    globalAgent.post(`/streams/streams/${streamId}/train/`, {
        expected_updated_at: expectedUpdatedAt,
        examples,
    });

export interface StreamsResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: Stream[];
}

export const getStreams = (signal?: AbortSignal): AxiosPromise<StreamsResponse | Stream[]> => {
    return globalAgent.get(`/streams/streams/`, { signal });
};

export interface LeadPackagePurchase {
    id: number;
    user: number;
    stream?: Stream | null;
    stream_id?: number;
    category?: LeadCategory | null;
    purchase_type: string;
    offer_code?: string | null;
    offer_name?: string | null;
    offer_snapshot?: Record<string, any> | null;
    package_size: number;
    credited_package_size?: number | null;
    price_per_lead: string | number;
    total_price: string | number;
    status: string;
    payment_id?: string | null;
    created_at: string;
    paid_at?: string | null;
}

export interface LeadPackagePurchasesResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: LeadPackagePurchase[];
}

export const getPurchases = (): AxiosPromise<LeadPackagePurchasesResponse | LeadPackagePurchase[]> => {
    return globalAgent.get(`/streams/purchases/`);
};

export const getPurchase = (id: number): AxiosPromise<LeadPackagePurchase> =>
    globalAgent.get(`/streams/purchases/${id}/`);

export interface PurchaseRequest {
    request_key?: string;
    package_size?: number;
    offer_id?: string;
    promo_code?: string;
    use_bonus_points?: boolean;
    use_wallet_rub?: boolean;
    auto_renewal?: boolean;
}

export interface PurchaseResponse {
    purchase_id: number;
    payment_id: string;
    confirmation_url: string;
    amount: {
        value: string;
        currency: string;
    };
    paid_immediately?: boolean;
    credited_package_size?: number;
    credited_access_duration_days?: number | null;
    credited_billing_period_months?: number | null;
    applied_promotion?: ActivePackagePromotion | null;
    applied_package_promotion?: ActivePackagePromotion | null;
}

export const purchasePackage = (streamId: number, data: PurchaseRequest): AxiosPromise<PurchaseResponse> => {
    return globalAgent.post(`/streams/streams/${streamId}/purchase/`, data);
};

export interface MonthlyLeadSubscription {
    status: 'pending' | 'active' | 'past_due' | 'cancelled';
    package_size: number;
    price_per_lead: string;
    total_price: string;
    protected_prices?: Array<{ package_size: number; price_per_lead: string; total_price: string }>;
    current_period_start: string | null;
    current_period_end: string | null;
    next_charge_at: string | null;
    canceled_at: string | null;
    last_failure: string;
    payment_processing?: boolean;
    confirmation_url?: string;
}

export interface MonthlyLeadStatus {
    available: boolean;
    subscription?: MonthlyLeadSubscription | null;
}

export const getMonthlyLeadStatus = (streamId: number): AxiosPromise<MonthlyLeadStatus> =>
    globalAgent.get(`/streams/streams/${streamId}/monthly/`);

export const cancelMonthlyLeadSubscription = (streamId: number): AxiosPromise<MonthlyLeadStatus> =>
    globalAgent.delete(`/streams/streams/${streamId}/monthly/`);

export const changeMonthlyLeadAllocation = (streamId: number, packageSize: number): AxiosPromise<MonthlyLeadStatus> =>
    globalAgent.patch(`/streams/streams/${streamId}/monthly/`, { package_size: packageSize });
